import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardText, Shield, Database, Bank, Globe, Briefcase, FirstAid, Users,
  CaretRight, Lightning, ChartLineUp, Warning, CheckCircle, Lock,
  Gauge, ArrowRight, Clock, Buildings
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Theme definitions with colors and icons
const DIAGNOSTIC_THEMES = [
  {
    id: 'cybersecurity',
    name: 'Cybercrime & Cyber Resilience',
    shortName: 'Cybersecurity',
    icon: Shield,
    color: '#3B82F6',
    description: 'Joint Standard 1 & 2 | Cybercrimes Act Compliance',
    questionsCount: 33,
    enabled: true,
    path: '/diagnostics/cybersecurity'
  },
  {
    id: 'data_protection',
    name: 'Data Protection & Privacy',
    shortName: 'Data Protection',
    icon: Database,
    color: '#10B981',
    description: 'POPIA Compliance Assessment',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/data-protection'
  },
  {
    id: 'financial_crime',
    name: 'Financial Crime & AML',
    shortName: 'Financial Crime',
    icon: Bank,
    color: '#F59E0B',
    description: 'FICA, FIC Act, AML/CFT Compliance',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/financial-crime'
  },
  {
    id: 'tax_compliance',
    name: 'Tax Compliance',
    shortName: 'Tax Compliance',
    icon: Globe,
    color: '#8B5CF6',
    description: 'SARS Tax Compliance Assessment',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/tax-compliance'
  },
  {
    id: 'corporate_governance',
    name: 'Corporate Governance',
    shortName: 'Corporate Governance',
    icon: Briefcase,
    color: '#EC4899',
    description: 'King V, Companies Act Compliance',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/corporate-governance'
  },
  {
    id: 'consumer_protection',
    name: 'Consumer Protection',
    shortName: 'Consumer Protection',
    icon: FirstAid,
    color: '#14B8A6',
    description: 'CPA, NCA Compliance Assessment',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/consumer-protection'
  },
  {
    id: 'employment_labour',
    name: 'Employment & Labour',
    shortName: 'Employment',
    icon: Users,
    color: '#F97316',
    description: 'BCEA, LRA, EEA Compliance',
    questionsCount: 0,
    enabled: false,
    comingSoon: true,
    path: '/diagnostics/employment-labour'
  }
];

// Theme Card Component
const ThemeCard = ({ theme, stats, onSelect }) => {
  const ThemeIcon = theme.icon;
  const hasAssessments = stats?.assessment_count > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative bg-background-surface border rounded-lg overflow-hidden transition-all ${
        theme.enabled 
          ? 'border-border hover:border-gold/50 cursor-pointer' 
          : 'border-border/50 opacity-60'
      }`}
      onClick={() => theme.enabled && onSelect(theme)}
    >
      {/* Coming Soon Badge */}
      {theme.comingSoon && (
        <div className="absolute top-3 right-3">
          <span className="px-2 py-1 bg-amber-500/10 text-amber-400 text-[10px] uppercase tracking-wider rounded flex items-center gap-1">
            <Clock size={10} />
            Coming Soon
          </span>
        </div>
      )}
      
      {/* Header */}
      <div 
        className="p-4"
        style={{ backgroundColor: `${theme.color}10` }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${theme.color}20` }}
          >
            <ThemeIcon size={24} style={{ color: theme.color }} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-primary">{theme.shortName}</h3>
            <p className="text-xs text-text-tertiary">{theme.description}</p>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="p-4 border-t border-border/50">
        {theme.enabled ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-lg font-mono font-bold" style={{ color: theme.color }}>
                {theme.questionsCount}
              </div>
              <div className="text-[10px] text-text-tertiary">Questions</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold text-text-primary">
                {stats?.assessment_count || 0}
              </div>
              <div className="text-[10px] text-text-tertiary">Assessments</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold text-text-primary">
                {stats?.overall_score ? `${stats.overall_score.toFixed(1)}` : '-'}
              </div>
              <div className="text-[10px] text-text-tertiary">Avg Score</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-xs text-text-tertiary py-2">
            <Lock size={12} />
            Assessment questions in development
          </div>
        )}
        
        {/* Action Button */}
        {theme.enabled && (
          <button
            className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 text-sm rounded transition-colors"
            style={{ 
              backgroundColor: `${theme.color}15`,
              color: theme.color
            }}
          >
            {hasAssessments ? 'View Assessments' : 'Start Assessment'}
            <ArrowRight size={14} />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Main Component
const DiagnosticsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [themeStats, setThemeStats] = useState({});
  
  // If a theme is specified in URL, redirect to that theme's page
  const themeParam = searchParams.get('theme');
  
  useEffect(() => {
    if (themeParam) {
      const theme = DIAGNOSTIC_THEMES.find(t => t.id === themeParam);
      if (theme?.enabled) {
        navigate(theme.path, { replace: true });
      }
    }
  }, [themeParam, navigate]);
  
  // Fetch stats for all themes
  const fetchStats = useCallback(async () => {
    try {
      // For now, only fetch cybersecurity stats
      const cyberRes = await axios.get(`${API}/cyber-diagnostics/dashboard`);
      
      setThemeStats({
        cybersecurity: {
          assessment_count: cyberRes.data.assessment_count || 0,
          overall_score: cyberRes.data.overall_score || null,
          flagged_items: cyberRes.data.flagged_items_count || 0
        }
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  const handleSelectTheme = (theme) => {
    if (theme.enabled) {
      navigate(theme.path);
    }
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Diagnostics...</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate totals
  const totalAssessments = Object.values(themeStats).reduce((sum, s) => sum + (s?.assessment_count || 0), 0);
  const enabledThemes = DIAGNOSTIC_THEMES.filter(t => t.enabled).length;
  
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center">
                <ClipboardText size={28} className="text-gold" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-medium text-text-primary">
                  Diagnostics Hub
                </h1>
                <p className="text-sm text-text-secondary">
                  Compliance maturity assessments across all regulatory themes
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-2xl font-mono font-semibold text-gold">{totalAssessments}</div>
                <div className="text-xs text-text-tertiary">Total Assessments</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div className="text-right">
                <div className="text-2xl font-mono font-semibold text-text-primary">{enabledThemes}/{DIAGNOSTIC_THEMES.length}</div>
                <div className="text-xs text-text-tertiary">Themes Active</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Links */}
        <div className="p-6 border-b border-border bg-background-surface/50">
          <div className="flex items-center gap-4">
            <Link
              to="/risk-log"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm rounded hover:bg-amber-500/20 transition-colors"
            >
              <Warning size={16} />
              Risk Log
            </Link>
            <Link
              to="/remediation-tracker"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm rounded hover:bg-emerald-500/20 transition-colors"
            >
              <CheckCircle size={16} />
              Remediation Tracker
            </Link>
            <Link
              to="/audit-intelligence"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm rounded hover:bg-purple-500/20 transition-colors"
            >
              <Lightning size={16} />
              Audit Intelligence
            </Link>
          </div>
        </div>
        
        {/* Theme Cards Grid */}
        <div className="p-6">
          <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            Select Regulatory Theme
          </h2>
          <div className="grid grid-cols-3 gap-6">
            {DIAGNOSTIC_THEMES.map((theme) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                stats={themeStats[theme.id]}
                onSelect={handleSelectTheme}
              />
            ))}
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="mx-6 mb-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Gauge size={20} className="text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-text-primary mb-1">How Diagnostics Work</h3>
              <p className="text-xs text-text-secondary">
                Each assessment uses a 0-4 maturity scoring model. Items scoring below 2 are automatically 
                flagged to the Risk Log. Items scoring 2 or above require evidence upload and NAVI AI validation. 
                Failed evidence items also go to the Risk Log. Rejected items are moved to the Remediation Tracker 
                for gap closure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsHubPage;
